import { useCallback, useEffect, useRef, useState } from 'react';
import API from '../../../../services/rest/api';
import { getDecryptedData } from '../../../../utils/common';
import type { Granularity, SufficiencyTrend } from '../types';

const FALLBACK_ERROR = 'Failed to fetch the sufficiency trend';

export interface UseSufficiencyTrendArgs {
  /** Coach portal: the patient to read. Omit in the app to read the logged-in patient. */
  patientId?: string;
  granularity: Granularity;
  from?: string;
  to?: string;
  /** Long ranges: backend slices bars; keep `from`/`to` stable and only change `page`. */
  page?: number;
}

/** The server answers 200 with `{ code, message, data }`; the body may also arrive encrypted as text. */
const unwrap = (res: any) => {
  if (typeof res !== 'string') return res;
  try {
    return JSON.parse(getDecryptedData(res));
  } catch {
    return null;
  }
};

export function useSufficiencyTrend({ patientId, granularity, from, to, page = 1 }: UseSufficiencyTrendArgs) {
  const [data, setData] = useState<SufficiencyTrend | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  // Only the latest request may write state, so a slow response for an old
  // granularity or range cannot overwrite a newer one.
  const latest = useRef(0);

  useEffect(() => {
    const id = ++latest.current;
    setLoading(true);
    setError(null);
    API.GetSufficiencyTrend({
      patient_id: patientId,
      granularity,
      from,
      to,
      ...(page > 1 ? { page } : {}),
    })
      .then((raw: any) => {
        if (id !== latest.current) return;
        const res = unwrap(raw);
        if (Number(res?.code) === 1 && res?.data) {
          setData(res.data);
        } else {
          setData(null);
          setError(res?.message || FALLBACK_ERROR);
        }
        setLoading(false);
      })
      .catch((err: any) => {
        if (id !== latest.current) return;
        setData(null);
        setError(err?.message || FALLBACK_ERROR);
        setLoading(false);
      });
  }, [patientId, granularity, from, to, page, nonce]);

  // Drop in-flight responses once the card unmounts.
  useEffect(() => () => void (latest.current += 1), []);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  return { data, loading, error, refetch };
}
